import React, { useState, useReducer } from 'react';
import Stage from './Stage';
import ResultPage from './ResultPage';
import { stages } from '../data/questions-pl';
import { evaluateStage } from '../utils/scoring';

const initialState = {
  currentStage: 0,
  testStarted: false,
  testCompleted: false,
  allAnswers: {},
  allQuestions: [],
  finalLevel: null,
  stageResults: []
};

function testReducer(state, action) {
  switch (action.type) {
    case 'START_TEST':
      return {
        ...state,
        testStarted: true,
        currentStage: 1
      };
    case 'COMPLETE_STAGE':
      const { evaluation, answers, questions } = action.payload;
      const newState = {
        ...state,
        allAnswers: { ...state.allAnswers, ...answers },
        allQuestions: [...state.allQuestions, ...questions],
        stageResults: [...state.stageResults, evaluation]
      };
      
      if (evaluation.finalLevel) {
        return {
          ...newState,
          testCompleted: true,
          finalLevel: evaluation.finalLevel
        };
      } else if (evaluation.shouldContinue) {
        return {
          ...newState,
          currentStage: evaluation.nextStage
        };
      }
      
      return newState;
    case 'RESTART':
      return initialState;
    default:
      return state;
  }
}

export default function TestEngine() {
  const [state, dispatch] = useReducer(testReducer, initialState);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleStart = () => {
    setShowInstructions(true);
  };
  
  const handleBeginTest = () => {
    dispatch({ type: 'START_TEST' });
  };
  
  const handleStageComplete = (score, totalQuestions, answers) => {
    setIsProcessing(true);
    
    setTimeout(() => {
      const currentStageData = stages[state.currentStage - 1];
      const evaluation = evaluateStage(score, totalQuestions, state.currentStage);
      
      dispatch({
        type: 'COMPLETE_STAGE',
        payload: {
          evaluation,
          answers,
          questions: currentStageData.questions
        }
      });
      
      setIsProcessing(false);
    }, 800);
  };
  
  const handleRestart = () => {
    dispatch({ type: 'RESTART' });
    setShowInstructions(false);
  };
  
  if (state.testCompleted) {
    return (
      <ResultPage
        finalLevel={state.finalLevel}
        allAnswers={state.allAnswers}
        allQuestions={state.allQuestions}
        onRestart={handleRestart}
      />
    );
  }
  
  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-700 font-semibold">Trwa przetwarzanie wyników...</p>
        </div>
      </div>
    );
  }
  
  if (state.testStarted) {
    const currentStageData = stages[state.currentStage - 1];
    return (
      <Stage
        key={`stage-${state.currentStage}`}
        stage={currentStageData}
        questions={currentStageData.questions}
        onComplete={handleStageComplete}
        totalStages={6}
      />
    );
  }
  
  if (showInstructions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Instrukcja testu</h2>
          
          <div className="space-y-4 mb-8 text-gray-700">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Struktura testu</h3>
              <p>To adaptacyjny test poziomujący (A1–C2). Każdy etap sprawdza gramatykę, słownictwo i czytanie.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-green-900 mb-2">✅ System adaptacyjny</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Wynik 80%+ → przejście na kolejny poziom</li>
                <li>Wynik 60–79% → to jest Twój poziom</li>
                <li>Wynik poniżej 60% → poziom niżej</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
              <h3 className="font-semibold text-amber-900 mb-2">⏱️ Limit czasu</h3>
              <p>Każdy etap ma limit 30 minut. Po upływie czasu odpowiedzi zostaną wysłane automatycznie.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <h3 className="font-semibold text-purple-900 mb-2">🔒 Ważne zasady</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Nie można wracać do poprzednich etapów</li>
                <li>Odpowiedz na wszystkie pytania przed wysłaniem</li>
                <li>Kolejność odpowiedzi jest losowa</li>
                <li>Test dopasowuje poziom do Twoich wyników</li>
              </ul>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowInstructions(false)}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
            >
              Wróć
            </button>
            <button
              onClick={handleBeginTest}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
            >
              Rozpocznij test
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="text-center max-w-2xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Test poziomujący z języka polskiego
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Sprawdź swój poziom CEFR (A1 - C2)
          </p>
          <p className="text-gray-500">
            Adaptacyjny test poziomujący dla uczących się języka polskiego
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level, index) => (
              <div
                key={level}
                className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white p-4 rounded-lg font-bold text-lg shadow-md"
              >
                {level}
              </div>
            ))}
          </div>
          
          <div className="space-y-3 text-left text-gray-700 mb-6">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <p className="font-semibold">Test adaptacyjny</p>
                <p className="text-sm text-gray-600">Poziom dostosowuje się do Twoich wyników</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏱️</span>
              <div>
                <p className="font-semibold">30 minut na etap</p>
                <p className="text-sm text-gray-600">Pracuj spokojnie, ale uważnie</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">🎯</span>
              <div>
                <p className="font-semibold">Szczegółowy wynik</p>
                <p className="text-sm text-gray-600">Otrzymasz poziom CEFR oraz analizę umiejętności</p>
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={handleStart}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-12 py-4 rounded-xl text-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-2xl"
        >
          Start testu
        </button>
      </div>
    </div>
  );
}
